import type { CSSProperties, FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FigLabel } from '@/components/generals/fig-label'
import { PageHeader } from '@/components/generals/page-header'
import { PrimaryButton } from '@/components/generals/primary-button'
import { Reveal } from '@/components/generals/reveal'
import { TextLink } from '@/components/generals/text-link'
import { ProcessSummary } from '@/components/process/process-summary'
import { ProcessTimeline } from '@/components/process/process-timeline'

import { MONITORING_YEARS, MONTHS_TO_HANDOVER, PROCESS_STAGES } from '@/constants/process'

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

const ProcessPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Process'
                description='How a Norrvik commission runs: six stages from the first site survey to ten years of monitoring, with what is measured, delivered and who is involved at each.'
                figure='Fig. 01 / Process / Six stages'
                shape='station'
            />

            <PageHeader
                fig='01'
                title='Process'
                detail='Six stages'
                note='Scroll to follow a commission'
                lines={['From a plot', 'to a decade', 'of data.']}
                intro={(
                    <p>
                        Every commission follows the same six stages. The site is scanned before the first sketch, every structural stage is checked on site, and the finished building keeps reporting for ten years.
                    </p>
                )}
                readouts={[
                    { label: 'Stages', value: String(PROCESS_STAGES.length).padStart(2, '0') },
                    { label: 'To handover', value: `About ${MONTHS_TO_HANDOVER} months` },
                    { label: 'Monitoring', value: `${MONITORING_YEARS} years` },
                    { label: 'Scans per project', value: 'About 60' }
                ]}
            />

            <section aria-labelledby='stages-title' className='pt-20 desk:pt-28'>
                <Reveal className='px-gutter pb-12 desk:pb-16'>
                    <FigLabel fig='02' title='Stages' detail='Brief to monitoring' note='The line fills as you scroll' />

                    <h2 id='stages-title' className='sr-only'>
                        The six stages of a commission
                    </h2>
                </Reveal>

                <ProcessTimeline />
            </section>

            <section aria-labelledby='summary-title' className='border-t border-hairline'>
                <Reveal className='px-gutter py-20 desk:py-28'>
                    <div className='grid gap-8 desk:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]'>
                        <div>
                            <FigLabel fig='03' title='Summary' detail='One commission at scale' note={`About ${MONTHS_TO_HANDOVER} months to handover / ${MONITORING_YEARS} years of monitoring`} />

                            <h2 id='summary-title' className='display-lg mt-10'>
                                <span className='reveal-line'>
                                    <span className='reveal-inner text-ink' style={delay(80)}>
                                        Two and a half years,
                                    </span>
                                </span>

                                <span className='reveal-line'>
                                    <span className='reveal-inner text-dim' style={delay(170)}>
                                        then ten more.
                                    </span>
                                </span>
                            </h2>
                        </div>

                        <div className='reveal-fade self-end desk:justify-self-end' style={delay(260)}>
                            <p className='body-copy text-ink/85'>
                                The first year is set out band by band on the Platform page, from the first scan to the year one review.
                            </p>

                            <TextLink href='/platform#survey-to-second-life' className='mt-6'>
                                The first year on the Platform timeline
                            </TextLink>
                        </div>
                    </div>

                    <div className='reveal-fade mt-16' style={delay(320)}>
                        <ProcessSummary />
                    </div>
                </Reveal>
            </section>

            <section aria-labelledby='process-next' className='border-t border-hairline'>
                <Reveal className='px-gutter pb-24 pt-20 desk:pb-32 desk:pt-28'>
                    <FigLabel fig='04' title='Next step' detail='Start with stage one' note='A survey can start within two weeks' />

                    <div className='mt-10 flex flex-col gap-10 desk:flex-row desk:items-end desk:justify-between'>
                        <h2 id='process-next' className='display-lg max-w-[18ch]'>
                            <span className='reveal-line'>
                                <span className='reveal-inner text-ink' style={delay(80)}>
                                    Every commission
                                </span>
                            </span>

                            <span className='reveal-line'>
                                <span className='reveal-inner text-dim' style={delay(170)}>
                                    starts with a scan.
                                </span>
                            </span>
                        </h2>

                        <div className='reveal-fade' style={delay(300)}>
                            <PrimaryButton href='/contact'>
                                Engage the studio
                            </PrimaryButton>
                        </div>
                    </div>
                </Reveal>
            </section>
        </Layout>
    )
}

export default ProcessPage
